import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../hooks/useAuth';
import { tagsOf } from '../../lib/memoryTags';
import { ISearch } from './DashIcons';

// Tag explorer — every tag your org has accumulated, with the count of
// memories each tag appears on (within the loaded window). Useful for
// noticing tag drift, finding tags worth pruning, or quickly grabbing a
// canonical name to use in a save.

const FETCH_LIMIT = 10000;

const TagsPage = () => {
  const { user } = useAuth();
  const [search, setSearch] = useState('');

  // /tags/all is the authoritative list — everything the user's corpus
  // has ever produced, sorted alphabetically. Counts are not on this
  // endpoint, so we cross-reference against the loaded memories window
  // for usage frequency.
  const { data: tagsData, isLoading: tagsLoading } = useQuery({
    queryKey: ['all-tags', user?.userId || user?.id],
    queryFn: () => apiService.getAllTags(),
    staleTime: 60000,
    enabled: !!user,
  });

  const { data: memoriesData } = useQuery({
    queryKey: ['memories-for-tags', user?.userId || user?.id],
    queryFn: () => apiService.getRecentMemories({ limit: FETCH_LIMIT }),
    staleTime: 60000,
    enabled: !!user,
  });

  const allMemories = useMemo(() => {
    const raw =
      memoriesData?.results ||
      memoriesData?.data?.blocks ||
      memoriesData?.blocks ||
      memoriesData?.data ||
      [];
    return Array.isArray(raw) ? raw : [];
  }, [memoriesData]);

  // Frequency map by tag label across the loaded memories window.
  const counts = useMemo(() => {
    const out = {};
    allMemories.forEach((m) => {
      tagsOf(m).forEach((t) => {
        out[t] = (out[t] || 0) + 1;
      });
    });
    return out;
  }, [allMemories]);

  const allTags = tagsData?.tags || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? allTags.filter((t) => t.toLowerCase().includes(q))
      : allTags;
    // Sort by count desc, then alphabetical.
    return [...list].sort((a, b) => {
      const ca = counts[a] || 0;
      const cb = counts[b] || 0;
      if (cb !== ca) return cb - ca;
      return a.localeCompare(b);
    });
  }, [allTags, counts, search]);

  const maxCount = Math.max(1, ...Object.values(counts));

  const onCopy = async (tag) => {
    try {
      await navigator.clipboard.writeText(tag);
      toast.success(`"${tag}" copied`);
    } catch {
      toast('Copy failed — select and copy manually', { icon: '·' });
    }
  };

  return (
    <>
      <Helmet><title>Tags · MME</title></Helmet>
      <div className="page">
        <div className="page-head">
          <h1>Tags</h1>
          <div className="sub">
            Every tag your org has accumulated. Counts come from the most
            recent {FETCH_LIMIT.toLocaleString()} saved memories — older
            usage isn&apos;t reflected. Click a tag to copy it.
          </div>
        </div>

        <div className="filter-row">
          <div className="search" style={{ flex: 1, maxWidth: 420 }}>
            <ISearch size={14} />
            <input
              type="text"
              placeholder="Filter tags…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Filter tags"
            />
          </div>
          <span className="filter-meta">
            {filtered.length.toLocaleString()} / {allTags.length.toLocaleString()} tags
          </span>
        </div>

        <div className="panel">
          {tagsLoading && (
            <div className="empty">
              <span className="spinner" style={{ marginRight: 8, verticalAlign: 'middle' }} />
              Loading tags…
            </div>
          )}
          {!tagsLoading && allTags.length === 0 && (
            <div className="empty">
              <div className="title">No tags yet</div>
              <div>Save some memories first — tags are extracted at save time.</div>
            </div>
          )}
          {!tagsLoading && filtered.length === 0 && allTags.length > 0 && (
            <div className="empty">No tags match your filter.</div>
          )}
          {!tagsLoading && filtered.length > 0 && (
            <table className="mtable">
              <thead>
                <tr>
                  <th>Tag</th>
                  <th style={{ width: 220 }}>Frequency in loaded window</th>
                  <th style={{ width: 80, textAlign: 'right' }}>Count</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((tag) => {
                  const c = counts[tag] || 0;
                  const pct = (c / maxCount) * 100;
                  return (
                    <tr
                      key={tag}
                      className="clickable"
                      onClick={() => onCopy(tag)}
                      title="Click to copy"
                    >
                      <td>
                        <code style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>{tag}</code>
                      </td>
                      <td>
                        <span
                          style={{
                            display: 'inline-block',
                            width: '100%',
                            height: 8,
                            background: 'var(--line)',
                            borderRadius: 4,
                            position: 'relative',
                          }}
                        >
                          <span
                            style={{
                              position: 'absolute',
                              inset: 0,
                              width: `${pct}%`,
                              background: c === 0 ? 'transparent' : 'var(--ink)',
                              borderRadius: 4,
                            }}
                          />
                        </span>
                      </td>
                      <td
                        style={{
                          textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums',
                          color: c === 0 ? 'var(--ink-4)' : 'inherit',
                        }}
                      >
                        {c}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
};

export default TagsPage;
